import InkindListItem from '@gik/ui/gik/InkindListItem';
import PopoverListItem from '@gik/ui/PopoverListItem';
import React from 'react';
import { gikClassPrefix } from '@gik/core/constants';
import { IInkindListItemProps, LoadingSpinner } from '@gik/ui';
import { useUserStore } from '@gik/core/store/UserStore';
import { useUserPages } from '@gik/api/inkinds/userPages';
import { isInkindPageOwnerOrOrganizer } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useInkinds } from '@gik/api/inkinds/inkinds';
import { getCDNAssetsUrl } from '@gik/core/utils/LinkUtils';
import { InkindPageViewHistoryItem } from '@gik/inkind-page';

const blockName = `${gikClassPrefix}-inkind-picker`;

export interface IInkindPickerContentProps {
  buyForSomeoneElseEnabled?: boolean;
  inkindPageViewHistory: InkindPageViewHistoryItem[];
  onInkindPageChosen(inkindRouteId: string): void;
  onBuyForSomeoneElse(): void;
}

export default function InkindPickerContent({
  onInkindPageChosen,
  onBuyForSomeoneElse,
  inkindPageViewHistory,
  buyForSomeoneElseEnabled,
}: IInkindPickerContentProps) {
  const historyRouteIds = inkindPageViewHistory ? inkindPageViewHistory.map(h => h.inkindRouteId) : [];
  const userStore = useUserStore();
  const userId = userStore.id;

  const { data: userPages, isValidating: isLoadingUserPages } = useUserPages(userId);
  const pageRouteIds = userPages?.map(page => page.routeId);

  const pageIds = [...(pageRouteIds || []), ...historyRouteIds.slice(0, 4)].filter(
    (v, i, a) => a.indexOf(v) === i // remove duplicates
  );

  const { data: inkindPages } = useInkinds(pageIds.length ? pageIds : null);

  const inkindListItems: IInkindListItemProps[] = inkindPages?.map(inkindPage => ({
    name: inkindPage.title,
    thumbnail: inkindPage.thumb_small,
    isOrganizer: isInkindPageOwnerOrOrganizer(inkindPage, userStore.id),
    routeId: inkindPage.routeId,
  }));

  const isLoading = !inkindListItems?.length && isLoadingUserPages;

  // keep original sorting
  inkindListItems?.sort((a, b) => pageIds.indexOf(a.routeId) - pageIds.indexOf(b.routeId));

  return (
    <div className={blockName}>
      <main>
        <h6>{buyForSomeoneElseEnabled ? 'BUYING THIS FOR A PAGE?' : 'BUY FOR A PAGE'}</h6>
        {buyForSomeoneElseEnabled && (
          <PopoverListItem
            prepend={<p className={`${blockName}__external-link`}>Buying for someone else</p>}
            icon="chevronRight"
            onClick={onBuyForSomeoneElse}
          />
        )}
        {isLoading && <LoadingSpinner center />}
        {inkindListItems?.map(inkindListItem => (
          <PopoverListItem
            hover
            key={inkindListItem.routeId}
            prepend={<InkindListItem {...inkindListItem} />}
            icon="chevronRight"
            onClick={() => onInkindPageChosen(inkindListItem.routeId)}
          />
        ))}
      </main>
      <footer className={`${blockName}__cannot-find-page`}>
        <p>Only pages you&apos;ve chosen to follow, or have recently visited are listed here.</p>
      </footer>
    </div>
  );
}
